import { createSlice } from '@reduxjs/toolkit';

import { NEW_FEATURE_ALERTS } from '@/constants';
import { NewFeatureAlert } from '@/types';

import { USER_PREFERENCES_SLICE_KEY } from './user-preferences.constants';
import {
  NewFeatureAlertsState,
  UserPreferencesState,
} from './user-preferences.types';

const newFeatureAlertsInitialState = Object.values(NEW_FEATURE_ALERTS).reduce(
  (acc, id) => {
    acc[id] = {
      isDismissed: false,
      firstSeenAt: undefined,
    };
    return acc;
  },
  {} as NewFeatureAlertsState,
);

export const initialState: UserPreferencesState = {
  playbackSpeed: 1,
  playbackVolume: 50,
  isGetStartedSectionDismissed: false,
  isShareWithAFriendDismissed: false,
  isTeamUpgradePromptDismissed: false,
  keepGoingPromoLastShownAt: null,
  dismissedEmailPreferenceWarnings: [],
  isExpandedPlayerGlowDismissed: false,
  isPdfPageControlsGlowDismissed: false,
  isPinnedFieldsOfStudyDismissed: false,
  isLpsListView: false,
  isTriennialSummaryExpanded: true,
  ...newFeatureAlertsInitialState,
};

const userPreferencesSlice = createSlice({
  name: USER_PREFERENCES_SLICE_KEY,
  initialState,
  reducers: {
    setPlaybackSpeedPreference: (state, { payload }: { payload: number }) => {
      state.playbackSpeed = payload;
    },
    setPlaybackVolumePreference: (state, { payload }: { payload: number }) => {
      state.playbackVolume = payload;
    },
    dismissGetStartedSection: state => {
      state.isGetStartedSectionDismissed = true;
    },
    dismissShareWithAFriend: state => {
      state.isShareWithAFriendDismissed = true;
    },
    dismissTeamUpgradePrompt: state => {
      state.isTeamUpgradePromptDismissed = true;
    },
    updateKeepGoingPromoLastShownAt: state => {
      state.keepGoingPromoLastShownAt = new Date().toISOString();
    },
    dismissNewFeatureAlert: (state, action: { payload: NewFeatureAlert }) => {
      state[action.payload].isDismissed = true;
    },
    markNewFeatureAlertSeen: (state, action: { payload: NewFeatureAlert }) => {
      if (state[action.payload].firstSeenAt) return;
      state[action.payload].firstSeenAt = new Date().toISOString();
    },
    resetNewFeatureAlert: (state, action: { payload: NewFeatureAlert }) => {
      state[action.payload].firstSeenAt = undefined;
      state[action.payload].isDismissed = false;
    },
    dismissEmailPreferenceWarning: (state, action: { payload: string }) => {
      if (!state.dismissedEmailPreferenceWarnings.includes(action.payload)) {
        state.dismissedEmailPreferenceWarnings.push(action.payload);
      }
    },
    dismissExpandedPlayerGlow: state => {
      state.isExpandedPlayerGlowDismissed = true;
    },
    dismissPdfPageControlsGlow: state => {
      state.isPdfPageControlsGlowDismissed = true;
    },
    dismissPinnedFieldsOfStudy: state => {
      state.isPinnedFieldsOfStudyDismissed = true;
    },
    setIsLpsListView: (state, { payload }: { payload: boolean }) => {
      state.isLpsListView = payload;
    },
    setIsTriennialSummaryExpanded: (
      state,
      { payload }: { payload: boolean },
    ) => {
      state.isTriennialSummaryExpanded = payload;
    },
    resetUserPreferences: () => initialState,
  },
});

export const userPreferencesActions = userPreferencesSlice.actions;
export const userPreferencesReducer = userPreferencesSlice.reducer;
export const USER_PREFERENCES_SLICE_INITIAL_STATE = initialState;
